import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageBody } from "../components/Layout";
import { Input } from "../components/ui/input";
import {
    RedditLogo, YoutubeLogo, TiktokLogo, InstagramLogo,
    MagnifyingGlass, Broadcast, Pause, CheckCircle, WarningCircle, Pulse,
} from "@phosphor-icons/react";

const PLATFORM_META = {
    Reddit:    { Icon: RedditLogo,    color: "#FF4500" },
    YouTube:   { Icon: YoutubeLogo,   color: "#FF0033" },
    TikTok:    { Icon: TiktokLogo,    color: "#0d0d0d" },
    Instagram: { Icon: InstagramLogo, color: "#C13584" },
};

const STATE_TONE = {
    "Configured":            { cls: "bg-muted text-muted-foreground",     Icon: WarningCircle },
    "Awaiting First Ingest": { cls: "bg-primary/10 text-primary",          Icon: Pulse },
    "Collecting":            { cls: "bg-primary text-primary-foreground", Icon: Broadcast },
    "Active":                { cls: "bg-emerald-500/15 text-emerald-700", Icon: CheckCircle },
    "Paused":                { cls: "bg-muted text-muted-foreground",     Icon: Pause },
    "Stalled":               { cls: "bg-accent/15 text-accent",            Icon: WarningCircle },
};

function StateChip({ state }) {
    const tone = STATE_TONE[state] || STATE_TONE["Configured"];
    const { Icon } = tone;
    return (
        <span
            data-testid={`overview-state-${String(state).replace(/\s+/g, "-").toLowerCase()}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-mono text-[10px] tracking-[0.14em] uppercase ${tone.cls}`}
        >
            <Icon size={10} weight="duotone" /> {state}
        </span>
    );
}

function fmtRelative(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function ListeningFor({ stream }) {
    // Show, in priority order, whatever the admin has configured.
    const topics      = stream.topics || [];
    const keywords    = stream.keywords || [];
    const hashtags    = (stream.hashtags && stream.hashtags.length) ? stream.hashtags : (stream.suggested_hashtags || []);
    const subreddits  = (stream.subreddits && stream.subreddits.length) ? stream.subreddits : (stream.suggested_communities || []);
    const creators    = (stream.creators && stream.creators.length) ? stream.creators : (stream.suggested_creators || []);
    const phrases     = stream.suggested_search_phrases || [];

    const groups = [
        { label: "Topics",      items: topics,     variant: "topic" },
        { label: "Phrases",     items: phrases,    variant: "soft", italic: true },
        { label: "Hashtags",    items: hashtags.map(h => h.startsWith("#") ? h : `#${h}`), variant: "soft" },
        { label: "Subreddits",  items: subreddits.map(s => s.startsWith("r/") ? s : `r/${s}`), variant: "soft" },
        { label: "Creators",    items: creators.map(c => c.startsWith("@") ? c : `@${c}`),     variant: "soft" },
        { label: "Keywords",    items: keywords,   variant: "soft" },
    ].filter((g) => g.items && g.items.length);

    if (!groups.length) {
        return (
            <p className="text-xs text-muted-foreground italic editorial">
                Not yet configured. An administrator hasn't told this stream what to listen for.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {groups.map((g) => (
                <div key={g.label} className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
                    <div className="overline text-[9px] text-muted-foreground/80 min-w-[78px]">{g.label}</div>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                        {g.items.slice(0, 14).map((item, i) => {
                            const isTopic = g.variant === "topic";
                            const cls = isTopic
                                ? "inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-normal bg-sidebar-bg text-white"
                                : `inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-normal border border-border bg-card text-foreground ${g.italic ? "italic" : ""}`;
                            return (
                                <span
                                    key={`${g.label}-${i}`}
                                    className={cls}
                                    data-testid={`overview-${g.label.toLowerCase()}-pill`}
                                >
                                    {item}
                                </span>
                            );
                        })}
                        {g.items.length > 14 && (
                            <span className="text-[10px] text-muted-foreground/70 font-mono self-center">
                                +{g.items.length - 14} more
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function PlatformRow({ platforms }) {
    if (!platforms?.length) return <span className="text-xs text-muted-foreground italic">No platforms selected</span>;
    return (
        <div className="flex items-center gap-2.5">
            {platforms.map((p) => {
                const meta = PLATFORM_META[p];
                if (!meta) return <span key={p} className="text-[10px] font-mono">{p}</span>;
                const { Icon, color } = meta;
                return (
                    <span key={p} className="inline-flex items-center gap-1" title={p}>
                        <Icon size={14} weight="fill" style={{ color }} />
                        <span className="text-[10px] font-mono tracking-wider text-muted-foreground uppercase">{p}</span>
                    </span>
                );
            })}
        </div>
    );
}

export default function StreamsOverview() {
    const [streams, setStreams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/streams");
                setStreams(r.data || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return streams;
        return streams.filter((s) => {
            const hay = [
                s.name, s.description,
                ...(s.topics || []), ...(s.keywords || []),
                ...(s.hashtags || []), ...(s.suggested_hashtags || []),
                ...(s.subreddits || []), ...(s.suggested_communities || []),
            ].join(" ").toLowerCase();
            return hay.includes(needle);
        });
    }, [streams, q]);

    const active = streams.filter((s) => s.active).length;
    const paused = streams.filter((s) => !s.active).length;

    return (
        <>
            <header data-testid="streams-overview-header" className="px-8 lg:px-14 pt-14 pb-8 border-b border-border">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-[68ch]">
                        <div className="overline mb-3 text-primary">HAEVN Labs · Listening Map</div>
                        <h1 className="font-display font-black tracking-[-0.025em] leading-[0.95] text-[clamp(2rem,3.6vw,3rem)]">
                            What we're listening to
                        </h1>
                        <p className="editorial text-muted-foreground mt-3 leading-relaxed">
                            These are the cultural conversations HAEVN Labs is continuously monitoring across Reddit,
                            TikTok, Instagram, and YouTube. Each stream defines a territory of language, topics, and
                            communities the system collects signals from. Stream configuration is handled by an
                            administrator.
                        </p>
                    </div>
                    <div className="flex items-center gap-6 font-mono text-[11px] tracking-wider uppercase text-muted-foreground">
                        <div data-testid="overview-count-total">
                            <span className="text-foreground font-semibold">{streams.length}</span> streams
                        </div>
                        <div data-testid="overview-count-active">
                            <span className="text-emerald-700 font-semibold">{active}</span> active
                        </div>
                        <div data-testid="overview-count-paused">
                            <span className="text-foreground/70 font-semibold">{paused}</span> paused
                        </div>
                    </div>
                </div>

                <div className="relative mt-8 max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        data-testid="overview-search"
                        placeholder="Search streams, topics, hashtags…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="rounded-sm pl-9"
                    />
                </div>
            </header>

            <PageBody>
                {loading ? (
                    <div className="px-8 lg:px-14 py-16 text-sm text-muted-foreground">Loading streams…</div>
                ) : filtered.length === 0 ? (
                    <div className="px-8 lg:px-14 py-16 text-sm text-muted-foreground italic editorial">
                        {streams.length === 0
                            ? "No streams have been configured yet. Once an administrator defines what to listen for, the streams will appear here."
                            : "No streams match your search."}
                    </div>
                ) : (
                    <div className="max-w-[1100px] mx-auto px-8 lg:px-14 py-12 space-y-6">
                        {filtered.map((s) => (
                            <article
                                key={s.id}
                                data-testid={`overview-stream-${s.id}`}
                                className="border border-border bg-card rounded-sm p-6 hover:border-foreground/30 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-6 flex-wrap mb-4">
                                    <div className="flex-1 min-w-[280px]">
                                        <div className="flex items-center gap-2.5 mb-1.5">
                                            <h2 className="font-display font-bold text-lg tracking-tight leading-tight">{s.name}</h2>
                                            <StateChip state={s.display_state || (s.active ? "Active" : "Paused")} />
                                        </div>
                                        {s.description && (
                                            <p className="editorial text-sm text-muted-foreground leading-relaxed max-w-[68ch]">
                                                {s.description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2 shrink-0">
                                        <PlatformRow platforms={s.platforms} />
                                        <div className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
                                            {(s.signal_count || 0).toLocaleString()} signals
                                            <span className="mx-1.5 text-border">·</span>
                                            last update {fmtRelative(s.last_ingested_at)}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-border">
                                    <ListeningFor stream={s} />
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </PageBody>
        </>
    );
}
